import { PublicBusRoute } from '@webapp/bff/src/types/public-transport';
import { useMemo } from 'react';
import {
  wktToPointArray,
  intersectsWithArea,
  LatLng,
  isPointInArea,
} from '@lib/gis';
import { usePublicBusDataInBounds } from './usePublicBusDataInBounds';

export type SegmentBusRoute = PublicBusRoute & {
  firstStopId: string;
  lastStopId: string;
};

const getSegments = (
  route: { stopId: string; lat: number; lng: number }[],
  area: LatLng[]
) => {
  const segments: {
    firstStop: string;
    lastStop: string;
  }[] = [];
  let currentSegment: {
    firstStop: string;
    lastStop?: string;
  } | null = null;

  for (let i = 0; i < route.length; i += 1) {
    const point = route[i];
    const isInArea = isPointInArea(point, area);
    if (isInArea && !currentSegment) {
      currentSegment = {
        firstStop: point.stopId,
      };
    } else if (!isInArea && currentSegment) {
      currentSegment!.lastStop = route[i - 1].stopId;
      segments.push(currentSegment as { firstStop: string; lastStop: string });
      currentSegment = null;
    }
  }
  if (
    currentSegment &&
    currentSegment.firstStop !== route[route.length - 1].stopId
  ) {
    segments.push({
      firstStop: currentSegment.firstStop,
      lastStop: route[route.length - 1].stopId,
    });
  }
  return segments;
};

export function useSearchPublicBusData(
  params: {
    siteId: string;
    searchAreaWkt: string;
  } | null
) {
  const { siteId, searchAreaWkt } = params || {};
  const points = searchAreaWkt ? wktToPointArray(searchAreaWkt) : [];

  const publicTransportDataHandler = usePublicBusDataInBounds({
    siteId,
    bounds: searchAreaWkt
      ? {
          north: Math.max(...points.map((p) => p.lat)),
          south: Math.min(...points.map((p) => p.lat)),
          east: Math.max(...points.map((p) => p.lng)),
          west: Math.min(...points.map((p) => p.lng)),
        }
      : undefined,
  });
  const routes = useMemo(
    () =>
      publicTransportDataHandler.data?.busRoutes && searchAreaWkt
        ? publicTransportDataHandler.data?.busRoutes
            .filter((r) =>
              intersectsWithArea(r.wkt, wktToPointArray(searchAreaWkt))
            )
            .map((r) => {
              const segments = getSegments(
                r.stops.map((s) => ({
                  stopId: s.stopId,
                  lat: s.lat,
                  lng: s.lon,
                })),
                wktToPointArray(searchAreaWkt)
              );
              return segments.map((s) => ({
                routeId: `${r.routeId}-${s.firstStop}-${s.lastStop}`,
                busServiceId: r.routeId,
                externalId: r.externalId,
                externalSource: r.externalSource,
                routeName: r.routeName,
                wkt: r.wkt,
                firstStopId: s.firstStop,
                lastStopId: s.lastStop,
                stops: r.stops,
              }));
            })
            .flat()
        : undefined,
    [publicTransportDataHandler.data, searchAreaWkt]
  );
  return {
    data: routes,
    isValidating: publicTransportDataHandler.isValidating,
    error: publicTransportDataHandler.error,
  };
}
